import {
  MID_ADVERBS,
  PARTICIPLES,
} from "@/lib/tool-kit/style-checkers/a-lexicon";
import {
  singleSpaced,
  type Word,
  wordsIn,
} from "@/lib/tool-kit/style-checkers/a-words";
import {
  countWords,
  headingFlags,
  insideAny,
  quotedSpans,
  ratePer,
  type Span,
  splitSentences,
  type Tally,
  tally,
} from "@/lib/tool-kit/writing-analysis/text";

/**
 * Forms of "to be" — am, is, are, was, were, be, been, being, and the contractions 'm, 're
 * and 's — each with the way it is used and, where the phrase is a known detour, a verb
 * that could replace the whole thing.
 *
 * Be-verbs are not errors. They define, describe and form the passive and progressive
 * tenses, and a text with none would be strange. What the page offers is a count, a
 * density and a list of the places where a be-verb may be hiding a stronger verb.
 *
 * Kinds are decided from the words that follow, without a parser:
 * - existential: "there is/are…" (the subject comes after the verb);
 * - expletive: "it is important that…" (a dummy "it" delays the real subject);
 * - passive: be + a past participle ("was written", "can be seen");
 * - progressive: be + an -ing verb ("is running");
 * - linking: everything else ("is a student", "was tired", "is important").
 */

export type BeKind =
  | "existential"
  | "expletive"
  | "passive"
  | "progressive"
  | "linking";

export interface BeMatch extends Span {
  /** The text highlighted: the be-verb, or the whole phrase when a replacement applies. */
  text: string;
  /** Lowercase base form: am, is, are, was, were, be, been or being. */
  form: string;
  kind: BeKind;
  /** The verb to try in place of the whole phrase ("is an indication of" → "indicate"). */
  replacement: string | null;
  sentenceStart: number;
}

export interface BeSentence extends Span {
  text: string;
  count: number;
}

export interface BeReport {
  words: number;
  sentences: number;
  matches: BeMatch[];
  perHundred: number;
  /** Sentences with at least one be-verb. */
  sentencesWith: number;
  /** Share of sentences with at least one be-verb, 0–1. */
  shareWith: number;
  perSentence: number;
  byForm: Tally[];
  byKind: Record<BeKind, number>;
  /** Sentences with three or more be-verbs, richest first. */
  heavy: BeSentence[];
  /** The "is a reflection of"-style detours found, with the verb to try. */
  replacements: Tally[];
}

/* ------------------------------------------------------------------ forms */

const FORM_OF = new Map<string, string>([
  ["am", "am"],
  ["is", "is"],
  ["are", "are"],
  ["was", "was"],
  ["were", "were"],
  ["be", "be"],
  ["been", "been"],
  ["being", "being"],
  ["isn't", "is"],
  ["aren't", "are"],
  ["wasn't", "was"],
  ["weren't", "were"],
  ["ain't", "am"],
  ["i'm", "am"],
  ["you're", "are"],
  ["we're", "are"],
  ["they're", "are"],
  ["he's", "is"],
  ["she's", "is"],
  ["it's", "is"],
  ["that's", "is"],
  ["there's", "is"],
  ["what's", "is"],
  ["who's", "is"],
  ["here's", "is"],
  ["where's", "is"],
  ["how's", "is"],
]);

/** After these, a contracted "'s" is "has", not "is": "it's been", "she's got". */
const HAS_FOLLOWERS: ReadonlySet<string> = new Set([
  "been",
  "got",
  "gotten",
  "gone",
  "had",
]);

const RE_UPPER_WORD = /^\p{Lu}+$/u;
const RE_NUMBER = /^\p{N}/u;
const RE_ING = /^\p{L}{3,}ing$/u;
const RE_ED = /^\p{L}{3,}ed$/u;
const RE_LY = /^\p{L}{3,}ly$/u;

const AUX_BEFORE: ReadonlySet<string> = new Set([
  "has",
  "have",
  "had",
  "will",
  "would",
  "can",
  "could",
  "may",
  "might",
  "must",
  "shall",
  "should",
  "not",
  "also",
  "never",
  "always",
  "often",
  "still",
  "just",
  "to",
]);

/** -ing words that are nouns or adjectives, so "is X" is linking, not progressive. */
const NOT_PROGRESSIVE: ReadonlySet<string> = new Set([
  "thing",
  "nothing",
  "something",
  "anything",
  "everything",
  "king",
  "ring",
  "bring",
  "spring",
  "string",
  "swing",
  "wing",
  "during",
  "morning",
  "evening",
  "ceiling",
  "building",
  "meaning",
  "ending",
  "sibling",
  "interesting",
  "surprising",
  "amazing",
  "exciting",
  "boring",
  "challenging",
  "promising",
  "outstanding",
  "striking",
  "convincing",
  "misleading",
  "daunting",
  "appealing",
  "pressing",
  "lasting",
  "existing",
  "following",
  "ongoing",
  "willing",
  "caring",
  "charming",
  "confusing",
  "disappointing",
  "frightening",
  "worrying",
]);

/** Participles used as plain adjectives: "I am tired" describes, it does not passivize. */
const ADJECTIVAL: ReadonlySet<string> = new Set([
  "tired",
  "excited",
  "interested",
  "worried",
  "concerned",
  "pleased",
  "surprised",
  "satisfied",
  "bored",
  "confused",
  "married",
  "scared",
  "shocked",
  "disappointed",
  "annoyed",
  "amazed",
  "exhausted",
  "determined",
  "qualified",
  "experienced",
  "advanced",
  "detailed",
  "balanced",
  "motivated",
  "educated",
  "talented",
  "gifted",
  "retired",
  "excited",
  "thrilled",
  "relieved",
  "embarrassed",
  "frustrated",
  "overwhelmed",
  "prepared",
  "supposed",
]);

/** -ed words that are not participles. */
const NOT_PARTICIPLE: ReadonlySet<string> = new Set([
  "hundred",
  "sacred",
  "naked",
  "wicked",
  "rugged",
  "indeed",
  "kindred",
  "hatred",
  "need",
  "speed",
  "seed",
  "feed",
  "bed",
  "red",
]);

/** The word after "it is" that makes "it" a dummy subject: "it is important that…". */
const EXPLETIVE_COMPLEMENTS: ReadonlySet<string> = new Set([
  "important",
  "essential",
  "necessary",
  "possible",
  "impossible",
  "likely",
  "unlikely",
  "clear",
  "evident",
  "obvious",
  "apparent",
  "true",
  "false",
  "difficult",
  "hard",
  "easy",
  "worth",
  "vital",
  "critical",
  "crucial",
  "notable",
  "noteworthy",
  "interesting",
  "unfortunate",
  "fortunate",
  "surprising",
  "reasonable",
  "recommended",
  "suggested",
  "argued",
  "claimed",
  "believed",
  "known",
  "thought",
  "said",
  "shown",
  "found",
  "noted",
  "expected",
  "estimated",
  "assumed",
  "widely",
  "generally",
  "commonly",
  "often",
  "time",
  "a",
  "an",
]);

const NEXT_COMPLEMENT_KEYWORDS: ReadonlySet<string> = new Set([
  "that",
  "to",
  "whether",
  "when",
  "how",
  "if",
]);

/* ------------------------------------------------------------------ replacements */

/**
 * "is a [adj] NOUN PREP" → the verb that noun was made from. The key is "noun prep".
 * Each value is a bare verb phrase; the page tells the reader to fit the tense.
 */
const NOUN_PHRASE_VERBS = new Map<string, string>([
  ["indication of", "indicate"],
  ["reflection of", "reflect"],
  ["demonstration of", "demonstrate"],
  ["illustration of", "illustrate"],
  ["example of", "illustrate"],
  ["representation of", "represent"],
  ["expression of", "express"],
  ["measure of", "measure"],
  ["function of", "depend on"],
  ["result of", "result from"],
  ["consequence of", "follow from"],
  ["cause of", "cause"],
  ["source of", "produce"],
  ["sign of", "signal"],
  ["symptom of", "signal"],
  ["reminder of", "remind readers of"],
  ["contribution to", "contribute to"],
  ["factor in", "affect"],
  ["influence on", "influence"],
  ["barrier to", "block"],
  ["threat to", "threaten"],
  ["means of", "enable"],
  ["author of", "write"],
  ["creator of", "create"],
  ["member of", "belong to"],
  ["part of", "belong to"],
  ["agreement with", "agree with"],
  ["conflict with", "conflict with"],
  ["need of", "need"],
  ["favor of", "favor"],
  ["support of", "support"],
  ["violation of", "violate"],
  ["opposition to", "oppose"],
  ["addition to", "add to"],
  ["possession of", "have"],
  ["control of", "control"],
  ["charge of", "run"],
  ["capable of", "can"],
  ["able to", "can"],
  ["responsible for", "cause"],
  ["dependent on", "depend on"],
  ["reliant on", "rely on"],
  ["indicative of", "indicate"],
  ["reflective of", "reflect"],
  ["representative of", "represent"],
  ["suggestive of", "suggest"],
  ["supportive of", "support"],
  ["illustrative of", "illustrate"],
  ["characteristic of", "characterize"],
  ["symptomatic of", "signal"],
  ["consistent with", "match"],
  ["aware of", "know"],
  ["fond of", "like"],
  ["afraid of", "fear"],
  ["jealous of", "envy"],
  ["desirous of", "want"],
]);

const RE_COMPLEMENT =
  /^\s+(?:(?:not|also|only|simply|just|never|always)\s+)?(?:(?:an?|the)\s+(?:\p{L}+\s+)?|in\s+(?:the\s+)?)?(\p{L}+)\s+(of|to|in|for|with|on|from)(?![\p{L}])/iu;

/** The phrase after a be-verb that has a verb inside it, if there is one. */
function replacementAfter(
  text: string,
  end: number
): { replacement: string; phraseEnd: number } | null {
  const match = RE_COMPLEMENT.exec(text.slice(end, end + 80));
  if (!match) {
    return null;
  }
  const replacement = NOUN_PHRASE_VERBS.get(
    `${match[1].toLowerCase()} ${match[2].toLowerCase()}`
  );
  return replacement ? { replacement, phraseEnd: end + match[0].length } : null;
}

/* ------------------------------------------------------------------ classification */

function isAllCapsAbbreviation(text: string): boolean {
  return text.length <= 3 && RE_UPPER_WORD.test(text);
}

/** The word the be-verb belongs to, looking back past auxiliaries and adverbs. */
function subjectBefore(tokens: readonly Word[], index: number): string {
  for (let i = index - 1; i >= Math.max(0, index - 4); i--) {
    const word = tokens[i].lower;
    if (!(AUX_BEFORE.has(word) || MID_ADVERBS.has(word) || RE_LY.test(word))) {
      return word;
    }
  }
  return "";
}

/** The first word after the be-verb that is not a negation or an adverb. */
function nextContent(tokens: readonly Word[], index: number): Word | undefined {
  let j = index + 1;
  while (
    j < tokens.length &&
    j <= index + 3 &&
    (MID_ADVERBS.has(tokens[j].lower) || RE_LY.test(tokens[j].lower))
  ) {
    j++;
  }
  return tokens[j];
}

function isPassiveParticiple(word: string): boolean {
  if (NOT_PARTICIPLE.has(word) || ADJECTIVAL.has(word)) {
    return false;
  }
  return PARTICIPLES.has(word) || (RE_ED.test(word) && word.length > 4);
}

function startsExpletiveComplement(
  tokens: readonly Word[],
  index: number
): boolean {
  const next = nextContent(tokens, index);
  if (!(next && EXPLETIVE_COMPLEMENTS.has(next.lower))) {
    return false;
  }
  // "it is important that / to / whether…" — the real subject follows within a few words.
  const window = tokens.slice(index + 1, index + 9);
  return window.some((word) => NEXT_COMPLEMENT_KEYWORDS.has(word.lower));
}

function classify(tokens: readonly Word[], index: number): BeKind {
  const form = tokens[index].lower;
  const subject = subjectBefore(tokens, index);
  if (subject === "there" || form === "there's") {
    return "existential";
  }
  const dummy = subject === "it" || form === "it's";
  if (dummy && startsExpletiveComplement(tokens, index)) {
    return "expletive";
  }
  const next = nextContent(tokens, index)?.lower ?? "";
  if (next === "there") {
    return "existential";
  }
  if (next === "being" || (RE_ING.test(next) && !NOT_PROGRESSIVE.has(next))) {
    return "progressive";
  }
  return isPassiveParticiple(next) ? "passive" : "linking";
}

function beFormOf(tokens: readonly Word[], index: number): string | null {
  const word = tokens[index];
  const form = FORM_OF.get(word.lower);
  if (!form || isAllCapsAbbreviation(word.text)) {
    return null;
  }
  if (word.lower === "am" && RE_NUMBER.test(tokens[index - 1]?.text ?? "")) {
    return null;
  }
  if (
    word.lower.endsWith("'s") &&
    HAS_FOLLOWERS.has(tokens[index + 1]?.lower ?? "")
  ) {
    return null;
  }
  return form;
}

/* ------------------------------------------------------------------ report */

export interface BeOptions {
  /** Skip be-verbs inside quotation marks (someone else's words). Default true. */
  ignoreQuotes?: boolean;
}

const KINDS: readonly BeKind[] = [
  "existential",
  "expletive",
  "passive",
  "progressive",
  "linking",
];

export function findBeVerbs(text: string, options: BeOptions = {}): BeReport {
  const ignoreQuotes = options.ignoreQuotes ?? true;
  const quotes = ignoreQuotes ? quotedSpans(text) : [];
  const sentences = splitSentences(text);
  const headings = headingFlags(sentences);
  const matches: BeMatch[] = [];
  const heavy: BeSentence[] = [];
  let counted = 0;
  let sentencesWith = 0;
  for (const [i, sentence] of sentences.entries()) {
    if (headings[i]) {
      continue;
    }
    counted++;
    const found = matchesIn(text, sentence.start, sentence.end, quotes);
    matches.push(...found);
    if (found.length > 0) {
      sentencesWith++;
    }
    if (found.length >= 3) {
      heavy.push({
        start: sentence.start,
        end: sentence.end,
        text: sentence.text,
        count: found.length,
      });
    }
  }
  return buildReport(text, counted, sentencesWith, matches, heavy);
}

function matchesIn(
  text: string,
  start: number,
  end: number,
  quotes: readonly Span[]
): BeMatch[] {
  const tokens = wordsIn(text, start, end);
  const found: BeMatch[] = [];
  for (const [index, word] of tokens.entries()) {
    const form = beFormOf(tokens, index);
    if (!form || insideAny(quotes, word.start, word.end)) {
      continue;
    }
    const phrase = replacementAfter(text, word.end);
    const phraseEnd = phrase ? Math.min(phrase.phraseEnd, end) : word.end;
    found.push({
      start: word.start,
      end: phraseEnd,
      text: text.slice(word.start, phraseEnd),
      form,
      kind: classify(tokens, index),
      replacement: phrase?.replacement ?? null,
      sentenceStart: start,
    });
  }
  return found;
}

function buildReport(
  text: string,
  sentences: number,
  sentencesWith: number,
  matches: BeMatch[],
  heavy: BeSentence[]
): BeReport {
  const words = countWords(text);
  const byKind = Object.fromEntries(KINDS.map((kind) => [kind, 0])) as Record<
    BeKind,
    number
  >;
  for (const match of matches) {
    byKind[match.kind]++;
  }
  return {
    words,
    sentences,
    matches,
    perHundred: ratePer(matches.length, words, 100),
    sentencesWith,
    shareWith: sentences > 0 ? sentencesWith / sentences : 0,
    perSentence: sentences > 0 ? matches.length / sentences : 0,
    byForm: tally(matches.map((match) => match.form)),
    byKind,
    heavy: heavy.sort((a, b) => b.count - a.count || a.start - b.start),
    replacements: tally(
      matches.flatMap((match) =>
        match.replacement
          ? [`${singleSpaced(match.text).toLowerCase()}|${match.replacement}`]
          : []
      )
    ),
  };
}
